import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from './api';

function clsx(...xs) { return xs.filter(Boolean).join(' '); }

function Badge({ children, tone = 'slate' }) {
  const map = {
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
    green: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    red: 'bg-rose-100 text-rose-700 border-rose-200',
    amber: 'bg-amber-100 text-amber-800 border-amber-200',
    blue: 'bg-sky-100 text-sky-700 border-sky-200',
    purple: 'bg-purple-100 text-purple-700 border-purple-200',
  };
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border', map[tone] || map.slate)}>
      {children}
    </span>
  );
}

function Button({ children, onClick, variant = 'primary', disabled = false, className = '', type='button' }) {
  const base = 'px-4 py-2 rounded-xl text-sm font-semibold transition-colors inline-flex items-center justify-center';
  const styles =
    variant === 'primary'
      ? 'bg-sky-700 text-white hover:bg-sky-800 disabled:opacity-60'
      : variant === 'danger'
      ? 'bg-rose-700 text-white hover:bg-rose-800 disabled:opacity-60'
      : 'bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 disabled:opacity-60';
  return (
    <button type={type} disabled={disabled} onClick={onClick} className={clsx(base, styles, className)}>
      {children}
    </button>
  );
}

function Modal({ open, title, children, onClose, wide=false }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[250] bg-black/40 flex items-center justify-center p-3">
      <div className={clsx('bg-white w-full rounded-2xl shadow-xl border', wide ? 'max-w-3xl' : 'max-w-xl')}>
        <div className="p-4 border-b flex items-center justify-between gap-3">
          <div className="font-semibold text-slate-900">{title}</div>
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const s = String(status || 'draft');
  if (s === 'locked') return <Badge tone="purple">locked</Badge>;
  if (s === 'submitted') return <Badge tone="green">submitted</Badge>;
  return <Badge tone="amber">draft</Badge>;
}

function pad2(n) { return String(n).padStart(2, '0'); }
function formatDTShort(dtStr) {
  if (!dtStr) return '—';
  const d = new Date(dtStr);
  if (Number.isNaN(d.getTime())) return String(dtStr).replace('T', ' ').slice(0, 16);
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

function statusTone(status) {
  const s = String(status || 'draft');
  if (s === 'locked') return 'purple';
  if (s === 'submitted') return 'green';
  return 'amber';
}

const DISCIPLINE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'PROG_EI', label: 'PROG_EI' },
  { value: 'PROG_PI', label: 'PROG_PI' },
  { value: 'PROG_ABA', label: 'PROG_ABA' },
  { value: 'PROG_OT', label: 'PROG_OT' },
  { value: 'PROG_PT', label: 'PROG_PT' },
  { value: 'PROG_ST', label: 'PROG_ST' },
];

export default function TeacherInstruments() {
  const navigate = useNavigate();

  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(''); // teacher chooses

  // filters for existing list
  const [periodFilter, setPeriodFilter] = useState('intake'); // intake|exit
  const [statusFilter, setStatusFilter] = useState('draft'); // draft|all|submitted|locked

  // template filters
  const [formKind, setFormKind] = useState('all'); // all|instrument|intake
  const [discipline, setDiscipline] = useState('all');
  const [q, setQ] = useState('');

  const [available, setAvailable] = useState([]);
  const [pickInstrumentId, setPickInstrumentId] = useState('');

  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);

  // confirm modal
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [pendingStart, setPendingStart] = useState(null);
  // pendingStart = { instrumentId, period, instrument_code, instrument_name, version }

  const selectedStudent = useMemo(
    () => students.find((s) => String(s.id) === String(studentId)) || null,
    [students, studentId]
  );

  function studentLabel(st) {
    if (!st) return '—';
    const ln = (st.last_name || '').trim();
    const fn = (st.first_name || '').trim();
    const pt = (st.program_type || '').trim();
    const name = `${ln}${ln && fn ? ', ' : ''}${fn}`.trim() || 'Unnamed';
    return pt ? `${name} (${pt})` : name;
  }

  async function loadStudents() {
    try {
      const res = await API.get('/me/students');
      const arr = Array.isArray(res.data) ? res.data : (res.data?.students || []);
      setStudents(arr);
    } catch (e) {
      console.error(e);
      setStudents([]);
    }
  }

  async function loadAvailable() {
    setLoading(true);
    try {
      const params = { active: 1 };
      if (formKind !== 'all') params.form_kind = formKind;
      if (discipline !== 'all') params.discipline_code = discipline;

      const res = await API.get('/instruments/available', { params });
      const items = res.data?.items || [];
      const usableItems = items.filter((x) => x.can_use);

      setAvailable(usableItems);

      const firstUsable = usableItems[0];
      if (!pickInstrumentId) {
        setPickInstrumentId(firstUsable ? String(firstUsable.id) : '');
      } else if (pickInstrumentId && !usableItems.some((x) => String(x.id) === String(pickInstrumentId))) {
        setPickInstrumentId(firstUsable ? String(firstUsable.id) : '');
      }
    } catch (e) {
      console.error(e);
      setAvailable([]);
      setPickInstrumentId('');
    } finally {
      setLoading(false);
    }
  }

  async function loadSubmissions(sid = studentId) {
    if (!sid) { setSubmissions([]); return; }
    setLoading(true);
    try {
      const res = await API.get(`/students/${Number(sid)}/instrument-submissions`, {
        params: { period: periodFilter, status: statusFilter },
      });
      setSubmissions(res.data?.submissions || []);
    } catch (e) {
      console.error(e);
      setSubmissions([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadStudents(); }, []); // eslint-disable-line
  useEffect(() => { loadAvailable(); }, [formKind, discipline]); // eslint-disable-line
  useEffect(() => { loadSubmissions(studentId); }, [studentId, periodFilter, statusFilter]); // eslint-disable-line

  const filteredSubmissions = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return submissions || [];
    return (submissions || []).filter((s) => (
      String(s.instrument_code || '').toLowerCase().includes(t) ||
      String(s.instrument_name || '').toLowerCase().includes(t) ||
      String(s.status || '').toLowerCase().includes(t) ||
      String(s.form_kind || '').toLowerCase().includes(t) ||
      String(s.discipline_code || '').toLowerCase().includes(t)
    ));
  }, [submissions, q]);

  const usableTemplates = useMemo(() => (available || []).filter((x) => x.can_use), [available]);

  // map (instrument_id|period) -> latest submission (for confirm)
  const submissionsByKey = useMemo(() => {
    const m = new Map();
    for (const s of submissions || []) {
      const k = `${s.instrument_id}|${s.period}`;
      const prev = m.get(k);
      if (!prev) m.set(k, s);
      else {
        const tPrev = new Date(prev.started_at || prev.updated_at || 0).getTime();
        const tNow = new Date(s.started_at || s.updated_at || 0).getTime();
        if (tNow >= tPrev) m.set(k, s);
      }
    }
    return m;
  }, [submissions]);

  function openStartConfirm(nextPeriod) {
    if (!studentId) return alert('Select student first');
    if (!pickInstrumentId) return alert('Select a usable template first');

    const picked = available.find((x) => String(x.id) === String(pickInstrumentId));
    if (!picked) return alert('Template not found');
    if (!picked.can_use) return alert(picked.cannot_reason || 'Not licensed');

    setPendingStart({
      instrumentId: Number(pickInstrumentId),
      period: nextPeriod,
      instrument_code: picked.instrument_code,
      instrument_name: picked.name,
      version: picked.version,
    });
    setConfirmOpen(true);
  }

  const confirmExisting = useMemo(() => {
    if (!pendingStart) return null;
    return submissionsByKey.get(`${pendingStart.instrumentId}|${pendingStart.period}`) || null;
  }, [pendingStart, submissionsByKey]);

  async function doStartSubmission() {
    if (!studentId) return alert('Select student first');
    if (!pendingStart) return;

    setConfirmBusy(true);
    try {
      const res = await API.post('/instrument-submissions/start', {
        student_id: Number(studentId),
        instrument_id: Number(pendingStart.instrumentId),
        period: pendingStart.period,
      });
      const sid = res.data?.submission_id;
      if (!sid) throw new Error('Missing submission_id');

      await loadSubmissions(studentId);

      setConfirmOpen(false);
      setPendingStart(null);

      navigate(`/teacher/instrument-submissions/${sid}`);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error || e.message || 'Failed to start');
    } finally {
      setConfirmBusy(false);
    }
  }

  function openSubmission(id) {
    navigate(`/teacher/instrument-submissions/${id}`);
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      {/* Neutral header */}
      <div className="bg-white border rounded-2xl shadow-sm p-4">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xl font-semibold text-slate-900 truncate">Instruments & Intakes</div>
            <div className="mt-1 text-sm text-slate-600">
              Teacher view: start a form for your student, or continue an existing draft.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-start lg:justify-end">
            <Button variant="secondary" onClick={loadAvailable} disabled={loading}>
              {loading ? 'Loading…' : 'Reload templates'}
            </Button>
            <Button variant="secondary" onClick={() => loadSubmissions(studentId)} disabled={!studentId || loading}>
              Refresh submissions
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-2">
          <div className="lg:col-span-5">
            <label className="text-xs text-slate-600">Student</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
            >
              <option value="">Select student…</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {studentLabel(s)}
                </option>
              ))}
            </select>
          </div>

          {/* Filters for existing list */}
          <div className="lg:col-span-2">
            <label className="text-xs text-slate-600">Period</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
            >
              <option value="intake">intake</option>
              <option value="exit">exit</option>
            </select>
          </div>

          <div className="lg:col-span-2">
            <label className="text-xs text-slate-600">Submissions</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="draft">draft</option>
              <option value="all">all</option>
              <option value="submitted">submitted</option>
              <option value="locked">locked</option>
            </select>
          </div>

          <div className="lg:col-span-3">
            <label className="text-xs text-slate-600">Search</label>
            <input
              className="w-full px-3 py-2 rounded-xl border bg-white text-slate-900"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="code/name/status…"
            />
          </div>
        </div>

        {selectedStudent ? (
          <div className="mt-3 text-sm text-slate-600">
            Selected: <span className="font-semibold text-slate-900">{studentLabel(selectedStudent)}</span>
          </div>
        ) : null}
      </div>

      {/* Start new */}
      <div className="mt-4 bg-white border rounded-2xl shadow-sm p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div>
            <div className="font-semibold text-slate-900">Start new</div>
            <div className="text-xs text-slate-500">
              Only templates with <span className="font-mono">can_use=true</span> can be started.
            </div>
          </div>

          {/* Option A: two explicit buttons */}
          <div className="flex flex-wrap gap-2 justify-start md:justify-end">
            <Button
              variant="primary"
              onClick={() => openStartConfirm('intake')}
              disabled={loading || !studentId || !pickInstrumentId || !usableTemplates.length}
            >
              Start intake
            </Button>
            <Button
              variant="secondary"
              onClick={() => openStartConfirm('exit')}
              disabled={loading || !studentId || !pickInstrumentId || !usableTemplates.length}
            >
              Start exit
            </Button>
          </div>
        </div>

        <div className="mt-3 grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-3">
            <label className="text-xs text-slate-600">Form kind</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white"
              value={formKind}
              onChange={(e) => setFormKind(e.target.value)}
            >
              <option value="all">all</option>
              <option value="instrument">instrument</option>
              <option value="intake">intake</option>
            </select>
          </div>

          <div className="md:col-span-3">
            <label className="text-xs text-slate-600">Discipline</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white"
              value={discipline}
              onChange={(e) => setDiscipline(e.target.value)}
            >
              {DISCIPLINE_OPTIONS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
            <div className="text-[11px] text-slate-500 mt-1">
              (Optional) Filters templates by discipline license / instrument discipline.
            </div>
          </div>

          <div className="md:col-span-6">
            <label className="text-xs text-slate-600">Template</label>
            <select
              className="w-full px-3 py-2 rounded-xl border bg-white"
              value={pickInstrumentId}
              onChange={(e) => setPickInstrumentId(e.target.value)}
            >
              {!available.length ? <option value="">No templates</option> : null}
              {available.map((i) => (
                <option key={i.id} value={i.id}>
                  ✅ [{i.form_kind}] {i.instrument_code} v{i.version} — {i.name}
                </option>
              ))}
            </select>
            {!usableTemplates.length ? (
              <div className="text-xs text-rose-700 mt-2">
                No usable templates for the current filters (license/discipline gating).
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Existing submissions */}
      <div className="mt-4 bg-white border rounded-2xl shadow-sm overflow-hidden">
        <div className="p-3 border-b flex items-center justify-between">
          <div className="font-semibold text-slate-900">Existing submissions</div>
          <Button variant="secondary" onClick={() => loadSubmissions(studentId)} disabled={!studentId || loading}>
            Refresh
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full text-sm">
            <thead className="bg-slate-50 border-b">
              <tr className="text-left">
                <th className="p-3">Template</th>
                <th className="p-3">Kind</th>
                <th className="p-3">Period</th>
                <th className="p-3">Status</th>
                <th className="p-3">Started</th>
                <th className="p-3">Submitted</th>
                <th className="p-3 text-right">Open</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubmissions.map((s) => (
                <tr key={s.id} className="border-b hover:bg-slate-50">
                  <td className="p-3">
                    <div className="font-mono text-[12px] text-slate-700">
                      {s.instrument_code} <span className="text-slate-500">v{s.version}</span>
                    </div>
                    <div className="font-medium text-slate-900">{s.instrument_name}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-1">
                      {s.discipline_code ? <Badge>{s.discipline_code}</Badge> : <span className="text-slate-400">—</span>}
                      <Badge tone={s.distribution_mode === 'shell_only' ? 'slate' : 'green'}>
                        {s.distribution_mode}
                      </Badge>
                    </div>
                  </td>
                  <td className="p-3"><Badge tone={s.form_kind === 'intake' ? 'purple' : 'blue'}>{s.form_kind}</Badge></td>
                  <td className="p-3"><Badge tone="amber">{s.period}</Badge></td>
                  <td className="p-3"><StatusBadge status={s.status} /></td>
                  <td className="p-3">{formatDTShort(s.started_at)}</td>
                  <td className="p-3">{formatDTShort(s.submitted_at)}</td>
                  <td className="p-3 text-right">
                    <Button variant="primary" onClick={() => openSubmission(s.id)} className="px-3 py-1.5 rounded-lg">
                      Open
                    </Button>
                  </td>
                </tr>
              ))}

              {!filteredSubmissions.length && !loading ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-slate-500">
                    {studentId ? 'No submissions found for this student/filters.' : 'Select a student to see submissions.'}
                  </td>
                </tr>
              ) : null}

              {loading ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-slate-500">Loading…</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <div className="p-3 text-xs text-slate-500">
          Teacher can edit only sections where owner_roles allow it. Final enforcement is on server.
        </div>
      </div>

      {/* Confirm modal */}
      <Modal
        open={confirmOpen}
        title={pendingStart ? `Confirm: Start ${pendingStart.period}` : 'Confirm'}
        onClose={() => { if (!confirmBusy) { setConfirmOpen(false); setPendingStart(null); } }}
      >
        {!pendingStart ? (
          <div className="text-sm text-slate-600">—</div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-slate-700">
              You are about to <span className="font-semibold">start/resume</span> a <Badge tone="amber">{pendingStart.period}</Badge> submission for:
            </div>

            <div className="border rounded-2xl p-3 bg-slate-50">
              <div className="text-xs text-slate-500">Student</div>
              <div className="font-semibold text-slate-900">{studentLabel(selectedStudent)}</div>
            </div>

            <div className="border rounded-2xl p-3 bg-slate-50">
              <div className="text-xs text-slate-500">Template</div>
              <div className="font-mono text-[12px] text-slate-700">
                {pendingStart.instrument_code} <span className="text-slate-500">v{pendingStart.version}</span>
              </div>
              <div className="font-semibold text-slate-900">{pendingStart.instrument_name}</div>
            </div>

            {confirmExisting ? (
              <div className="border rounded-2xl p-3 bg-amber-50 border-amber-200">
                <div className="text-sm text-amber-900 font-semibold">Already exists for this student</div>
                <div className="mt-1 text-sm text-amber-900">
                  Status: <Badge tone={statusTone(confirmExisting.status)}>{confirmExisting.status}</Badge>
                  <span className="ml-2 text-amber-800">
                    Started: {formatDTShort(confirmExisting.started_at)}
                  </span>
                </div>
                <div className="mt-2 text-xs text-amber-800">
                  Confirm will typically resume an existing draft (no duplicates), but please verify.
                </div>
              </div>
            ) : (
              <div className="border rounded-2xl p-3 bg-emerald-50 border-emerald-200">
                <div className="text-sm text-emerald-900 font-semibold">No existing submission found</div>
                <div className="text-xs text-emerald-800 mt-1">
                  This will create a new draft submission for this student.
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" disabled={confirmBusy} onClick={() => { setConfirmOpen(false); setPendingStart(null); }}>
                Cancel
              </Button>
              <Button variant="primary" disabled={confirmBusy} onClick={doStartSubmission}>
                {confirmBusy ? 'Starting…' : 'Confirm'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}



